import { memo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Smartphone, Laptop, Briefcase, ArrowRight } from "lucide-react";
import { useScrollReveal } from "@/hooks/useAnimations";

const ServiceCard = ({ to, testid, delay, img, alt, Icon, iconBg, title, desc, badges, visible }) => (
  <Link
    to={to}
    className="group"
    data-testid={testid}
    style={{ transitionDelay: visible ? delay : "0s" }}
  >
    <Card className="h-full overflow-hidden card-lift card-shine border-slate-200">
      <div className="relative h-48 overflow-hidden img-zoom">
        <img src={img} alt={alt} className="w-full h-full object-cover" loading="lazy" width="600" height="192" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
        <div className={`absolute top-4 left-4 ${iconBg} text-white p-2 rounded-lg rotate-hover`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
      <CardContent className="p-6">
        <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-red-600 transition-colors underline-animate">
          {title}
        </h3>
        <p className="text-slate-600 mb-4">{desc}</p>
        <div className="flex flex-wrap gap-2">
          {badges.map((b) => (
            <Badge key={b} variant="secondary" className="text-xs">{b}</Badge>
          ))}
        </div>
        <div className="mt-4 flex items-center text-red-600 font-medium text-sm group-hover:gap-2 transition-all">
          En savoir plus
          <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
        </div>
      </CardContent>
    </Card>
  </Link>
);

export const ServicesSection = memo(() => {
  const [servicesRef, servicesVisible] = useScrollReveal(0.1);

  return (
    <section className="section-padding bg-white">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Nos Services de Réparation</h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Experts en réparation depuis plus de 10 ans à Niort et ses alentours
          </p>
        </div>

        <div
          ref={servicesRef}
          className={`grid md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-1000 ${
            servicesVisible ? "opacity-100" : "opacity-0"
          }`}
        >
          <ServiceCard
            to="/reparation-telephone"
            testid="service-card-reparation-telephone"
            delay="0.1s"
            img="https://images.unsplash.com/photo-1746005514010-e7ab2bc3d210?w=600"
            alt="Réparation Téléphone"
            Icon={Smartphone}
            iconBg="bg-blue-500"
            title="Réparation Téléphone & Tablette"
            desc="iPhone, Samsung, Xiaomi, Huawei, Oppo, Honor, Google Pixel, iPad, Galaxy Tab. Tous modèles, intervention express."
            badges={["iPhone", "Samsung", "Xiaomi", "Pixel", "iPad"]}
            visible={servicesVisible}
          />
          <ServiceCard
            to="/reparation-ordinateur"
            testid="service-card-reparation-ordinateur"
            delay="0.2s"
            img="https://images.unsplash.com/photo-1540589522742-82a91dac6fa0?w=600"
            alt="Réparation Ordinateur"
            Icon={Laptop}
            iconBg="bg-emerald-500"
            title="Réparation Ordinateur & Console"
            desc="MacBook, iMac, PC portable, ordinateur fixe, PS5, Xbox, Nintendo Switch. Diagnostic gratuit et garantie 6 mois."
            badges={["MacBook", "PC", "PS5", "Xbox", "Switch"]}
            visible={servicesVisible}
          />
          <ServiceCard
            to="/services-professionnels"
            testid="service-card-services-professionnels"
            delay="0.3s"
            img="https://customer-assets.emergentagent.com/job_b23b68ca-96f4-4732-840a-c86194f2a639/artifacts/h8vphp0g_IMGP9221.png"
            alt="Services Professionnels Mediastore"
            Icon={Briefcase}
            iconBg="bg-purple-500"
            title="Services Professionnels"
            desc="Contrats maintenance, intervention sur site, solutions IT entreprises."
            badges={["Maintenance", "Support", "Formation"]}
            visible={servicesVisible}
          />
        </div>
      </div>
    </section>
  );
});

ServicesSection.displayName = "ServicesSection";
